import React, { useState, useEffect, useMemo } from "react";
import modalStyles from "./styles.module.css";
import { toast } from "react-toastify";
import moment from "moment";

const AddEventModal = ({ setAddEventModal, slots = [], addSlots, getAvailability }) => {
  const [loading, setLoading] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);

  // Initialize times from the passed slot
  useEffect(() => {
    if (slots.length > 0 && slots[0].from) {
      const slotDate = new Date(slots[0].from);
      setSelectedDate(slotDate);
      setStartTime(moment(slots[0].from).format("HH:mm"));
      setEndTime(moment(slots[0].to).format("HH:mm"));
    }
  }, [slots]);

  // Generate time options in 30-minute intervals
  const generateTimeOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    const isToday = selectedDate && moment(selectedDate).isSame(now, 'day');

    for (let hour = 0; hour < 24; hour++) {
      for (let min of [0, 30]) {
        const timeString = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;

        // If it's today, only show future times
        if (isToday) {
          const timeDate = new Date(selectedDate);
          const [h, m] = timeString.split(':').map(Number);
          timeDate.setHours(h, m, 0, 0);

          if (timeDate <= now) {
            continue; // Skip past times
          }
        }

        const h12 = hour % 12 || 12;
        const ampm = hour < 12 ? "AM" : "PM";
        const label = `${h12}:${min.toString().padStart(2, '0')} ${ampm}`;

        options.push({ value: timeString, label });
      }
    }
    return options;
  }, [selectedDate]);

  // Generate end time options based on start time
  const endTimeOptions = useMemo(() => {
    if (!startTime) return [];

    const [startHour, startMin] = startTime.split(':').map(Number);
    const options = [];

    // Add 30 minutes option
    const time30 = moment(selectedDate).hours(startHour).minutes(startMin).add(30, 'minutes');
    if (time30.hours() < 24) {
      const h12 = time30.hours() % 12 || 12;
      const ampm = time30.hours() < 12 ? "AM" : "PM";
      options.push({
        value: time30.format("HH:mm"),
        label: `${h12}:${time30.format('mm')} ${ampm} (30 min)`
      });
    }

    // Add 60 minutes option
    const time60 = moment(selectedDate).hours(startHour).minutes(startMin).add(60, 'minutes');
    if (time60.hours() < 24) {
      const h12 = time60.hours() % 12 || 12;
      const ampm = time60.hours() < 12 ? "AM" : "PM";
      options.push({
        value: time60.format("HH:mm"),
        label: `${h12}:${time60.format('mm')} ${ampm} (60 min)`
      });
    }

    return options;
  }, [startTime, selectedDate]);

  // Auto-select first end time option when start time changes
  useEffect(() => {
    if (endTimeOptions.length > 0 && !endTime) {
      setEndTime(endTimeOptions[0].value);
    }
  }, [endTimeOptions, endTime]);

  const handleClick = async () => {
    if (!startTime || !endTime) {
      toast.warning("Please select both start and end times!");
      return;
    }

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const fromDate = new Date(selectedDate);
    fromDate.setHours(startHour, startMin, 0, 0);

    const toDate = new Date(selectedDate);
    toDate.setHours(endHour, endMin, 0, 0);

    if (toDate <= fromDate) {
      toast.error("End time must be after start time!");
      return;
    }

    const updatedSlots = [{
      from: fromDate,
      to: toDate,
      sessionDate: selectedDate,
      teacherId: slots[0]?.teacherId
    }];

    setLoading(true);
    try {
      const result = await addSlots(updatedSlots);
      if (result?.success) {
        await getAvailability();
        toast.success("Slot added successfully!");
        setAddEventModal(false);
      } else {
        toast.error(result?.message || "Failed to add slot.");
      }
    } catch (err) {
      toast.error("An error occurred while adding slot.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={modalStyles.modalBackdrop}>
      <div className={modalStyles.modal}>
        {/* Header */}
        <div
          className={modalStyles.closeBtn}
          onClick={() => setAddEventModal(false)}
        >
          <i className="fas fa-close"></i>
        </div>
        <h2 className={modalStyles.modalHeading}>Add Availability</h2>

        {/* Body */}
        <div className={modalStyles.slots}>
          <div className={modalStyles.slot}>
            <h4>Selected Date</h4>
            <div className={modalStyles.dateDisplay}>
              {selectedDate ? moment(selectedDate).format("dddd, MMMM DD, YYYY") : "No date selected"}
            </div>

            <div className={modalStyles.formGroup}>
              <label>Start Time</label>
              <select
                className={modalStyles.timeSelect}
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setEndTime(""); // Reset end time when start time changes
                }}
              >
                <option value="">Select start time</option>
                {generateTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className={modalStyles.formGroup}>
              <label>End Time</label>
              <select
                className={modalStyles.timeSelect}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={!startTime}
              >
                <option value="">Select end time</option>
                {endTimeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {startTime && endTime && (
              <div className={modalStyles.summaryBox}>
                <div className={modalStyles.summaryTitle}>
                  <i className="fas fa-calendar-check"></i>
                  Slot Summary
                </div>
                <div className={modalStyles.summaryContent}>
                  <div className={modalStyles.summaryRow}>
                    <span>From</span>
                    <span>{moment(selectedDate).hours(startTime.split(':')[0]).minutes(startTime.split(':')[1]).format("hh:mm A")}</span>
                  </div>
                  <div className={modalStyles.summaryRow}>
                    <span>To</span>
                    <span>{moment(selectedDate).hours(endTime.split(':')[0]).minutes(endTime.split(':')[1]).format("hh:mm A")}</span>
                  </div>
                  <div className={modalStyles.summaryRow}>
                    <span>Duration</span>
                    <span>
                      {moment.duration(
                        moment(selectedDate).hours(endTime.split(':')[0]).minutes(endTime.split(':')[1])
                          .diff(moment(selectedDate).hours(startTime.split(':')[0]).minutes(startTime.split(':')[1]))
                      ).asMinutes()} minutes
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <button
          className={modalStyles.saveSlotBtn}
          onClick={handleClick}
          disabled={loading || !startTime || !endTime}
        >
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <i className="fas fa-spinner fa-spin"></i>
              Adding Slot...
            </div>
          ) : "Add Availability Slot"}
        </button>
      </div>
    </div>
  );
};

export default AddEventModal;
